/**
 * Running pace / speed zone helpers (TrainingPeaks-style race → threshold).
 */

export const DISTANCE_5K_M = 5000;
export const DISTANCE_10K_M = 10000;

const RACE_THRESHOLD_FRACTIONS: Record<number, number> = {
  1609: 0.92,
  5000: 0.943,
  10000: 0.971,
  21097.5: 0.985,
  42195: 1.0,
};

const RACE_BLEND_WEIGHTS: Record<number, number> = {
  5000: 0.35,
  10000: 0.65,
};

export function parseRaceTimeString(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const m = /^(\d+):(\d{1,2})$/.exec(trimmed);
  if (m) {
    const minutes = Number(m[1]);
    const seconds = Number(m[2]);
    if (seconds >= 60) return null;
    return minutes * 60 + seconds;
  }
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  return null;
}

export function formatPaceSeconds(secPerKm: number): string {
  if (!secPerKm || secPerKm <= 0 || !Number.isFinite(secPerKm)) return '—';
  const total = Math.round(secPerKm);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** km/h (workout form / summary) → seconds per km. */
export function paceSecPerKmFromKmh(kmh: number): number {
  if (!Number.isFinite(kmh) || kmh <= 0) return 0;
  return 3600 / kmh;
}

/** Seconds per km → km/h. */
export function kmhFromPaceSecPerKm(paceSecPerKm: number): number {
  if (!Number.isFinite(paceSecPerKm) || paceSecPerKm <= 0) return 0;
  return 3600 / paceSecPerKm;
}

/** Form display: km/h → m:ss/km (empty when speed is blank). */
export function formatPaceFromKmh(kmh: string | number | null | undefined): string {
  if (kmh === null || kmh === undefined || kmh === '') return '';
  const n = typeof kmh === 'string' ? Number.parseFloat(kmh.replace(',', '.')) : kmh;
  if (!Number.isFinite(n) || n <= 0) return '';
  const formatted = formatPaceSeconds(paceSecPerKmFromKmh(n));
  return formatted === '—' ? '' : formatted;
}

/** Parse m:ss pace input → km/h string for workout form state. */
export function kmhInputFromPaceString(pace: string): string {
  const trimmed = pace.trim();
  if (!trimmed) return '';
  const sec = parseRaceTimeString(trimmed);
  if (sec == null || sec <= 0) return '';
  const kmh = kmhFromPaceSecPerKm(sec);
  if (!Number.isFinite(kmh) || kmh <= 0) return '';
  const rounded = Math.round(kmh * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function speedMpsFromPaceSecondsPerKm(paceSecPerKm: number): number {
  if (paceSecPerKm <= 0) return 0;
  return 1000 / paceSecPerKm;
}

export function paceSecondsPerKmFromSpeedMps(speedMps: number): number {
  if (speedMps <= 0) return 0;
  return 1000 / speedMps;
}

export function fractionForDistance(distanceMeters: number): number {
  if (RACE_THRESHOLD_FRACTIONS[distanceMeters] != null) {
    return RACE_THRESHOLD_FRACTIONS[distanceMeters];
  }
  const keys = Object.keys(RACE_THRESHOLD_FRACTIONS)
    .map(Number)
    .sort((a, b) => a - b);
  if (distanceMeters <= keys[0]) return RACE_THRESHOLD_FRACTIONS[keys[0]];
  const last = keys[keys.length - 1];
  if (distanceMeters >= last) return RACE_THRESHOLD_FRACTIONS[last];
  for (let i = 0; i < keys.length - 1; i++) {
    const low = keys[i];
    const high = keys[i + 1];
    if (distanceMeters >= low && distanceMeters <= high) {
      const logPos =
        (Math.log(distanceMeters) - Math.log(low)) /
        (Math.log(high) - Math.log(low));
      return (
        RACE_THRESHOLD_FRACTIONS[low] +
        logPos * (RACE_THRESHOLD_FRACTIONS[high] - RACE_THRESHOLD_FRACTIONS[low])
      );
    }
  }
  return RACE_THRESHOLD_FRACTIONS[last];
}

export function thresholdSpeedFromRace(
  distanceMeters: number,
  timeSeconds: number,
): number {
  if (distanceMeters <= 0 || timeSeconds <= 0) return 0;
  const raceSpeed = distanceMeters / timeSeconds;
  return Math.round(raceSpeed * fractionForDistance(distanceMeters) * 10000) / 10000;
}

export function thresholdSpeedFromRaces(
  races: { distance: number; timeSeconds: number }[],
): number {
  const estimates: number[] = [];
  const weights: number[] = [];
  for (const race of races) {
    if (race.distance <= 0 || race.timeSeconds <= 0) continue;
    estimates.push(thresholdSpeedFromRace(race.distance, race.timeSeconds));
    weights.push(RACE_BLEND_WEIGHTS[race.distance] ?? 1);
  }
  if (!estimates.length) return 0;
  const totalW = weights.reduce((a, b) => a + b, 0);
  if (totalW <= 0) {
    return (
      Math.round(
        (estimates.reduce((a, b) => a + b, 0) / estimates.length) * 10000,
      ) / 10000
    );
  }
  let sum = 0;
  for (let i = 0; i < estimates.length; i++) {
    sum += estimates[i] * (weights[i] / totalW);
  }
  return Math.round(sum * 10000) / 10000;
}

export function formatZonePaceRange(minimumMps: number, maximumMps: number): string {
  if (!minimumMps || !maximumMps) return '—';
  const slowPace = paceSecondsPerKmFromSpeedMps(minimumMps);
  const fastPace = paceSecondsPerKmFromSpeedMps(maximumMps);
  return `${formatPaceSeconds(slowPace)} – ${formatPaceSeconds(fastPace)} /km`;
}

export function isDistanceTimeMethod(calculationTypeId?: number): boolean {
  return calculationTypeId === 7;
}

/**
 * Legacy production API (pre SpeedZoneInputResolver) requires `speed` in m/s.
 * New API accepts pace_seconds_per_km / race times — send both for compatibility.
 */
export function applyLegacySpeedApiFields(
  payload: Record<string, unknown>,
  speedMps: number,
  paceSecPerKm?: number,
): void {
  if (speedMps <= 0) return;
  payload.speed = speedMps;
  if (paceSecPerKm != null && paceSecPerKm > 0) {
    payload.pace_seconds_per_km = paceSecPerKm;
  }
}

/** Build PATCH /calculate-zones body for running pace zones. */
export function buildSpeedZoneCalculatePayload(
  method: { id: number; calculation_type_id?: number },
  race5k: string,
  race10k: string,
  thresholdPace: string,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    calculation_method: method.id,
    zone_type: 'speed',
  };

  if (isDistanceTimeMethod(method.calculation_type_id)) {
    if (race5k.trim()) base.race_5k_time = race5k.trim();
    if (race10k.trim()) base.race_10k_time = race10k.trim();

    const races: { distance: number; timeSeconds: number }[] = [];
    const t5 = parseRaceTimeString(race5k);
    const t10 = parseRaceTimeString(race10k);
    if (t5) races.push({ distance: DISTANCE_5K_M, timeSeconds: t5 });
    if (t10) races.push({ distance: DISTANCE_10K_M, timeSeconds: t10 });
    if (races.length) {
      const speedMps =
        races.length === 1
          ? thresholdSpeedFromRace(races[0].distance, races[0].timeSeconds)
          : thresholdSpeedFromRaces(races);
      applyLegacySpeedApiFields(base, speedMps);
    }
    return base;
  }

  const paceSec = parseRaceTimeString(thresholdPace);
  if (paceSec) {
    applyLegacySpeedApiFields(base, speedMpsFromPaceSecondsPerKm(paceSec), paceSec);
  }
  return base;
}
